import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat } from 'helpers/utils';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Avatar from 'components/base/Avatar';
import { Order } from 'data/e-commerce/orders';
import Badge from 'components/base/Badge';
import FeatherIcon from 'feather-icons-react';
import { FC } from 'react';
import Button from 'components/base/Button';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

// Create a type that includes all properties from ColumnDef<Order> plus visible property
// Use 'any' in some type parameters to ensure accessorKey and id properties are preserved
export type OrderColumnDef = ColumnDef<Order, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
}

// Create column function with translation capability
export const createOrdersTableColumns = (t: (key: string) => string): OrderColumnDef[] => [
  {
    accessorKey: 'orderId',
    header: t('tableHeaders.orderId'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { orderId } = original;
      return (
        <Link to="#!" className="fw-semibold">
          #{orderId}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    // Add Order Number column - based on the GraphQL query's order_number field
    id: 'order_number',
    accessorFn: (row: any) => `#${row.order_number}`,
    header: t('tableHeaders.orderNumber'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { orderId, order_number } = original;
      const path = "/orders/" + orderId;
      return (
        <Link to={path} className="fw-semibold">
          #{order_number}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 text-body fw-semibold' }
    }
  },
  {
    accessorKey: 'total',
    header: t('tableHeaders.totalAmount'),
    visible: true,
    cell: ({ row: { original } }) => currencyFormat(original.total),
    meta: {
      headerProps: { style: { width: '6%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
    }
  },
  {
    accessorKey: 'total_commission',
    header: t('tableHeaders.commission'),
    visible: true,
    cell: ({ row: { original } }) => currencyFormat(original.total_commission || 0),
    meta: {
      headerProps: { style: { width: '6%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
    }
  },
  {
    accessorKey: 'total_seller_amount',
    header: t('tableHeaders.netEarnings'),
    visible: true,
    cell: ({ row: { original } }) => currencyFormat(original.total_seller_amount || 0),
    meta: {
      headerProps: { style: { width: '6%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
    }
  },
  {
    id: 'customer',
    accessorFn: ({ customer }) => customer, // Access customer directly as a string
    header: t('tableHeaders.customerName'),
    visible: true,
    cell: ({ row: { original } }) => {
      // Display customer as plain text - handle both string and object types
      const customerName = typeof original.customer === 'string'
        ? original.customer
        : original.customer?.name || t('common.unknownCustomer');
      
      return (
        <p className="mb-0 text-body-emphasis fw-bold">{customerName}</p>
      );
    },
    meta: {
      headerProps: {
        style: { width: '18%', minWidth: 200 },
        className: 'ps-4'
      },
      cellProps: { className: 'ps-4 py-2 white-space-nowrap' }
    }
  },
  {
    id: 'payment_method',
    accessorFn: ({ payment_method }) => payment_method?.label || '',
    header: t('tableHeaders.paymentMethod'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { payment_method } = original;
      if (!payment_method) return null;
      
      // Special handling for payment methods with hardcoded mapping
      let displayLabel = '';
      
      // Direct mapping for known payment methods
      if (payment_method.label === 'Cash On Delivery') {
        displayLabel = t('paymentMethod.cashOnDelivery');
        // If translation returns the key itself, use original label
        if (displayLabel === 'paymentMethod.cashOnDelivery') {
          displayLabel = payment_method.label;
        }
      } else if (payment_method.label?.toLowerCase().includes('credit card')) {
        displayLabel = t('paymentMethod.creditCard');
        // If translation returns the key itself, use original label
        if (displayLabel === 'paymentMethod.creditCard') {
          displayLabel = payment_method.label;
        }
      } else {
        // For other payment methods, try to translate or use original
        displayLabel = payment_method.label;
      }
      
      return (
        <Badge
          bg={payment_method.type || 'info'}
          variant="phoenix"
          iconPosition="end"
          className="fs-10"
          icon={payment_method.icon ? (
            <FeatherIcon
              icon={payment_method.icon}
              size={12.8}
              className="ms-1"
            />
          ) : undefined}
        >
          {displayLabel}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    id: 'state',
    accessorFn: ({ state }) => state?.label || 'N/A',
    header: t('tableHeaders.state'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { state } = original;
      
      if (!state) {
        return (
          <Badge
            bg="secondary"
            variant="phoenix"
            className="fs-10"
          >
            {t('common.notAvailable') || 'N/A'}
          </Badge>
        );
      }
      
      // For state translation
      let displayLabel = '';
      const stateLabel = state.label?.toLowerCase() || '';
      const currentLanguage = i18n.language;
      
      // Handle translations differently based on language
      if (currentLanguage === 'en') {
        // For English, show the original label
        displayLabel = state.label;
      } else {
        // For other languages (French), translate using keys
        let translationKey = '';
        
        // Map common state labels to translation keys
        switch (stateLabel) {
          case 'completed':
          case 'complete': 
            translationKey = 'orderStatus.completed';
            break;
          case 'processing':
            translationKey = 'orderStatus.processing';
            break;
          case 'canceled':
            translationKey = 'orderStatus.canceled';
            break;
          case 'closed':
            translationKey = 'orderStatus.closed';
            break;
          default: {
            // Format the status label as a translation key
            const formattedKey = stateLabel.replace(/\s+/g, '_');
            translationKey = `orderStatus.${formattedKey}`;
            break;
          }
        }
        
        // Get translation
        displayLabel = t(translationKey);
        
        // If we got back the same key, something went wrong with translation, use original
        if (displayLabel === translationKey) {
          displayLabel = state.label;
        }
      }
      
      return (
        <Badge
          bg={state.type}
          variant="phoenix"
          iconPosition="end"
          className="fs-10"
          icon={
            <FeatherIcon
              icon={state.icon}
              size={12.8}
              className="ms-1"
            />
          }
        >
          {displayLabel}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    id: 'fulfilment_status',
    accessorFn: ({ fulfilment_status }) => fulfilment_status.label,
    header: t('tableHeaders.orderStatus'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { fulfilment_status } = original;
      
      if (!fulfilment_status) {
        return (
          <Badge
            bg="secondary"
            variant="phoenix"
            className="fs-10"
          >
            {t('common.notAvailable') || 'N/A'}
          </Badge>
        );
      }
      
      // For order status translation
      let displayLabel = '';
      const statusLabel = fulfilment_status.label?.toLowerCase() || '';
      const currentLanguage = i18n.language;
      
      // Handle translations differently based on language
      if (currentLanguage === 'en') {
        // For English, show the original label
        displayLabel = fulfilment_status.label;
      } else {
        // For other languages (French), translate using keys
        let translationKey = '';
        
        // Map common status labels to translation keys
        switch (statusLabel) {
          case 'being prepared':
          case 'en cours de preparation': 
            translationKey = 'orderStatus.being_prepared';
            break;
          case 'prepared':
            translationKey = 'orderStatus.prepared';
            break;
          case 'in transit':
          case 'en_cour_d_enlevement':
            translationKey = 'orderStatus.in_transit';
            break;
          case 'completed':
          case 'complete': 
            translationKey = 'orderStatus.completed';
            break;
          case 'processing':
            translationKey = 'orderStatus.processing';
            break;
          case 'canceled':
            translationKey = 'orderStatus.canceled';
            break;
          case 'closed':
            translationKey = 'orderStatus.closed';
            break;
            case 'enlever':
            translationKey = 'orderStatus.enlever';
            break;
          default: {
            // Format the status label as a translation key
            const formattedKey = statusLabel.replace(/\s+/g, '_');
            translationKey = `orderStatus.${formattedKey}`;
            break;
          }
        }
        
        // Get translation
        displayLabel = t(translationKey);
        
        // If we got back the same key, something went wrong with translation, use original
        if (displayLabel === translationKey) {
          displayLabel = fulfilment_status.label;
        }
      }
      
      return (
        <Badge
          bg={fulfilment_status.type}
          variant="phoenix"
          iconPosition="end"
          className="fs-10"
          icon={
            <FeatherIcon
              icon={fulfilment_status.icon}
              size={12.8}
              className="ms-1"
            />
          }
        >
          {displayLabel}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '12%', minWidth: 200 }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    accessorKey: 'delivery_type',
    header: t('tableHeaders.shippingMethod'),
    visible: true,
    meta: {
      headerProps: { style: { width: '30%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 text-body fs-9' }
    }
  },
  {
    accessorKey: 'date',
    header: t('tableHeaders.dateOrder'),
    visible: true,
    meta: {
      headerProps: { className: 'ps-4' },
      cellProps: {
        className: 'ps-4 text-body-tertiary fs-9 white-space-nowrap'
      }
    }
  },
  {
    id: 'transaction_link',
    header: t('tableHeaders.transaction'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { has_transaction_id, orderId, order_number } = original;
      if (!has_transaction_id) {
        return "-";
      }
      
      return (
        <Link 
          to='/transactions'
          state={{ order_number }}
          className="btn btn-sm btn-phoenix-warning"
          title={t('transactions.viewTransaction')}
        >
          <FeatherIcon icon="credit-card" size={14} />
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'ps-4 text-center' },
      cellProps: { className: 'ps-4 text-center' }
    }
  }
];

// Export the columns for backward compatibility
// This default export uses translation keys directly as fallback
export const ordersTableColumns = createOrdersTableColumns(key => key);

interface OrdersTableProps {
  visibleColumns?: Record<string, boolean>;
  onToggleColumn?: (columnId: string, visible: boolean) => void;
  totalCount?: number;
}

const OrdersTable: FC<OrdersTableProps> = ({ totalCount }) => {
  // Use translation hook
  const { t } = useTranslation();
  
  return (
    <div>
      <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
      <AdvanceTableFooter 
        pagination 
        totalCount={totalCount} 
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default OrdersTable;
